import { useEffect, useMemo, useRef, useState } from "react";
import { cleanText, hasKorean, pickKoreanTitle, relativeTime } from "../lib/utils";

function search(articles, q) {
  const lower = q.toLowerCase();
  return articles
    .filter(a => {
      const text = ((a.hed_ko || "") + " " + (a.hed || "") + " " + (a.summary || "") + " " + (a.src || "")).toLowerCase();
      return text.includes(lower);
    })
    .slice(0, 30);
}

export default function PCSearch({ articles = [], adversary = [], socmint = [], onClose, onSelect }) {
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const combined = useMemo(() => {
    const all = [...articles, ...adversary, ...socmint];
    return all;
  }, [articles, adversary, socmint]);

  const results = useMemo(() => q.trim().length >= 1 ? search(combined, q.trim()) : [], [q, combined]);

  useEffect(() => { setCursor(0); }, [q]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor(c => Math.min(results.length - 1, c + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor(c => Math.max(0, c - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = results[cursor];
      if (item) onSelect(item);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        paddingTop: 80,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "min(640px, 92%)",
          background: "var(--pc-bg-2)",
          border: "1px solid var(--pc-line-3)",
          maxHeight: "70vh",
          display: "flex", flexDirection: "column",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "16px 18px", borderBottom: "1px solid var(--pc-line-3)",
        }}>
          <span style={{ fontSize: 16, color: "var(--pc-fg-3)" }}>⌕</span>
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="키워드, 매체, 기자… (Esc로 닫기)"
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: "var(--pc-fg-1)", fontSize: 14,
              fontFamily: "var(--pc-font-sans)", minWidth: 0,
            }}
          />
          <button
            onClick={onClose}
            style={{
              background: "transparent", border: "1px solid var(--pc-line-3)",
              color: "var(--pc-fg-3)", fontSize: 10, fontWeight: 700,
              padding: "4px 8px", letterSpacing: ".08em", cursor: "pointer",
              fontFamily: "var(--pc-font-mono)",
            }}
          >ESC</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {q.trim().length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "var(--pc-fg-3)", fontSize: 12 }}>
              검색어를 입력하세요 — 뉴스·적대국 매체·SOCMINT 통합 검색.
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "var(--pc-fg-3)", fontSize: 12 }}>
              매칭 결과 없음.
            </div>
          ) : (
            results.map((a, i) => {
              const sel = i === cursor;
              const t = a.publishedAt ? relativeTime(a.publishedAt) : a.time;
              const ko = cleanText(pickKoreanTitle(a.hed_ko, a.hed));
              const en = hasKorean(a.hed_ko) ? cleanText(a.hed) : "";
              return (
                <div
                  key={a.id + "_" + i}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => onSelect(a)}
                  style={{
                    padding: "12px 18px",
                    background: sel ? "rgba(255,255,255,0.04)" : "transparent",
                    borderBottom: "1px solid var(--pc-line-3)",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "var(--pc-fg-2)", letterSpacing: ".05em" }}>{a.src}</span>
                    <span style={{ fontSize: 10, color: "var(--pc-fg-3)" }}>· {t}</span>
                    {a.cls && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: "var(--pc-fg-3)", textTransform: "uppercase", letterSpacing: ".08em" }}>
                        {a.cls}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13.5, color: "var(--pc-fg-1)", lineHeight: 1.4, fontWeight: sel ? 700 : 500 }}>
                    {ko}
                  </div>
                  {en && <div style={{ fontSize: 11, color: "var(--pc-fg-4)", lineHeight: 1.4, marginTop: 2 }}>{en}</div>}
                </div>
              );
            })
          )}
        </div>

        <div style={{
          padding: "8px 18px",
          borderTop: "1px solid var(--pc-line-3)",
          fontSize: 10, color: "var(--pc-fg-4)",
          letterSpacing: ".05em", fontFamily: "var(--pc-font-mono)",
          display: "flex", gap: 16,
        }}>
          <span>↑↓ 이동</span>
          <span>Enter 선택</span>
          <span>Esc 닫기</span>
          <span style={{ marginLeft: "auto" }}>{results.length}건</span>
        </div>
      </div>
    </div>
  );
}
